import type { Recipe } from "@mealplan/powersync";
import { parseStringArray } from "../recipes/queries";
import { buildDataset, type ExportColumn, type ExportDataset, type ExportMetadata } from "./types";

/**
 * Entity adapter for MealPlan: maps a list of `recipe` records into the generic
 * {@link ExportDataset}. This is the ONLY recipe-specific piece — it replaces
 * the canon todo adapter (see K1 propagation notes).
 *
 * Only recipe-row-local fields are exported. Ingredients live in the child
 * table `recipe_ingredient`, so they are intentionally NOT fanned out here: the
 * export is a flat, one-row-per-recipe table. `tags` (the recipe's category /
 * meal-type labels) are stored as a JSON string array and rendered as a
 * comma-separated list; `created_at` passes through as an ISO string.
 */

function formatTags(value: string | null | undefined): string {
  return parseStringArray(value).join(", ");
}

export const RECIPE_EXPORT_COLUMNS: ExportColumn<Recipe>[] = [
  { key: "name", header: "Name", accessor: (r) => r.name ?? "" },
  { key: "servings", header: "Servings", accessor: (r) => r.servings ?? "" },
  { key: "tags", header: "Tags", accessor: (r) => formatTags(r.tags) },
  { key: "notes", header: "Notes", accessor: (r) => r.notes ?? "" },
  { key: "created_at", header: "Created", accessor: (r) => r.created_at ?? "" },
];

/**
 * Builds an {@link ExportDataset} from a list of recipes, ready for
 * `datasetToCsv` / `datasetToHtml`.
 */
export function buildRecipeDataset(
  recipes: readonly Recipe[],
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(RECIPE_EXPORT_COLUMNS, recipes, {
    title: "Recipes",
    ...metadata,
  });
}
